import React, {Fragment, useEffect, useState} from "react";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import {
    addSupportedUnit,
    createSupportUnit, getRootSupportOrganizationUnit,
    getSupportedUnits,
    getSupportUnit, getSupportUnitCountry,
    removeSupportedUnit, setSupportUnitCountry
} from "../../api/support-unit-functions";
import {useTranslation} from "react-i18next";
import {useApiNotification} from "../ui/ApiNotificationProvider";
import SupportedUnits from "./SupportedUnits";
import {run} from "../../utils/function-util";
import {OrganizationsTableMultiPicker} from "../organizations/OrganizationsTableMultiPicker";
import {GreyedOutProgressMask} from "../ui/GreyedOutProgressMask";
import {SupportUnit as SupportingUnit, SupportedUnit, Country} from "../../models/domain-of-responsibility.models";
import {Organization} from "../../models/organization";
import {ApplyCountryCodeDialog} from "./ApplyCountryCodeDialog";
import {getOrganizationUnit} from "../../api/organization-unit-functions";

export const SupportUnit = ({
                                organizationId,
                                organizationUnitId
                            }: { organizationId: number, organizationUnitId: number }) => {
    const [supportUnit, setSupportUnit] = useState<SupportingUnit>();
    // `null` implies that the country has not been set
    // `undefined` implies that the country binding is not applicable here
    const [country, setCountry] = useState<Country | null | undefined>(undefined);
    const [refresh, setRefresh] = useState(0);
    const {t} = useTranslation();
    const [processing, setProcessing] = useState<boolean>(false);
    const [supportedUnits, setSupportedUnits] = useState<SupportedUnit[]>([]);
    const {showSuccess, handleError} = useApiNotification();
    const [addMode, setAddMode] = useState(false);
    const [open, setOpen] = useState(false);
    const supportedOrganizationIds = new Set(supportedUnits.map(u => u.organizationId));

    useEffect(run(
        () => supportUnit ? getSupportedUnits({supportUnitId: supportUnit.id}) : Promise.resolve([]),
        (suppUnits: SupportedUnit[]) => setSupportedUnits(suppUnits),
        handleError
    ), [supportUnit, refresh]);


    useEffect(run(
        () => organizationUnitId && getSupportUnit({organizationUnitId})
        , (su: SupportingUnit) => su && setSupportUnit(su)
        , handleError), [organizationUnitId]);

    useEffect(run(
        async () => {
            if (!supportUnit) return undefined;
            const root = await getRootSupportOrganizationUnit();
            const orgUnit = await getOrganizationUnit(organizationId, organizationUnitId);
            const rootOrgUnit = await getOrganizationUnit(organizationId, root.organizationUnitId);
            // only when current org-unit is a descendant of the root-support-org-unit,
            // then the country can be set (or loaded).
            if (orgUnit && orgUnit.internalCode.includes(rootOrgUnit.internalCode + "/")) {
                const c = await getSupportUnitCountry(supportUnit.id);
                return c ?? null;
            }
            return undefined;
        },
        (c: Country | null | undefined) => {
            setCountry(c);
        },
        handleError
    ), [supportUnit]);

    const activateSupportUnit = async () => {
        try {
            const newSupportUnit = await createSupportUnit({organizationUnitId});
            setSupportUnit(newSupportUnit);
            showSuccess();
        } catch (e) {
            handleError(e);
        }
    };

    const onAddSupportedOrganization = async (organizations: Map<number, Organization>) => {
        setProcessing(true);
        setAddMode(false);
        let allSuccess = true;
        if (!organizations) return;
        if (!supportUnit) return;

        for (const [, organization] of Array.from(organizations)) {
            try {
                await addSupportedUnit({
                    supportUnitId: supportUnit.id,
                    organizationId: organization.id
                });
            } catch (err) {
                allSuccess = false;
                handleError(err)
            }
        }

        setProcessing(false);
        if (allSuccess) showSuccess();
        setRefresh(r => r + 1);
    };

    const supportedUnitRemoveHandler = (supportedUnit: SupportedUnit): Promise<any> => {
        if (!supportUnit) return Promise.resolve();
        return removeSupportedUnit(supportUnit.id, supportedUnit.organizationId)
            .then(success => {
                if (success)
                    showSuccess(t('_Common.Api.Messages.SupportedUnitRemovalSuccess'))
            })
            .catch(handleError)
            .finally(() => {
                setRefresh(r => r + 1);
            });
    }

    const handleCountrySelected = (selectedCountry: Country | null): Promise<any> => {
        const op = !selectedCountry || selectedCountry.id === 0 || !supportUnit ? Promise.resolve() :
            setSupportUnitCountry(supportUnit.id, selectedCountry.id).then(() => setCountry(selectedCountry));
        return op
            .catch(handleError)
            .finally(() => setOpen(false));
    }

    const supportedOrganizationList = <Grid container item xs={12} justify="center">
        {
            !supportUnit ?
                <Button variant="contained"
                        color="primary"
                        size="medium"
                        onClick={activateSupportUnit}>
                    {t('SupportUnit.Actions.Activate')}
                </Button> :
                <Fragment>
                    <SupportedUnits
                        supportedUnits={supportedUnits}
                        onRemove={supportedUnitRemoveHandler}
                    />
                    <Grid container item
                          xs={12}
                          style={{marginTop: '2em'}}
                          alignItems="center"
                          direction="row-reverse"
                          justify="space-between">
                        <Button variant="contained"
                                color="primary"
                                size="medium"
                                onClick={() => setAddMode(true)}>
                            {t('SupportUnit.Actions.AddOrganizations')}
                        </Button>
                        {country === null ?
                            <Button variant="contained"
                                    color="primary"
                                    size="medium" onClick={() => setOpen(prev => !prev)}>
                                {t('SupportUnit.Actions.ApplyCountryCode')}
                            </Button> :
                            country &&
                            <Button variant="outlined" color="primary" size="medium">
                                {t('SupportUnit.Actions.CountryCode')}{country.name} ({country.twoLetterIsoCode})
                            </Button>

                        }
                    </Grid>
                    <ApplyCountryCodeDialog supportUnitId={supportUnit.id} open={open}
                                            onSelected={(selectedCountry) => handleCountrySelected(selectedCountry)}>
                    </ApplyCountryCodeDialog>
                </Fragment>
        }
    </Grid>;

    const addOrganizationsPicker = <OrganizationsTableMultiPicker
        onCancel={() => setAddMode(false)}
        preselectedIds={supportedOrganizationIds}
        onSave={onAddSupportedOrganization}
    />


    return (
        <GreyedOutProgressMask isActive={processing} displaySpinner={true}>
            <Grid container spacing={3}>
                {addMode ? addOrganizationsPicker : supportedOrganizationList}
            </Grid>
        </GreyedOutProgressMask>
    );
};
